import { createContext, useContext } from "react";

import type { StripeElementsMode } from "../../StripeElements/types";
import type { VerifyAttemptProps } from "./hooks/useVerifyAttempt";
import type {
  BasePaywallsCheckoutAttemptsContextProps,
  MemberAddressState,
  OnStageChange,
} from "./types";

export interface PaywallCheckoutAttemptsContextValue extends BasePaywallsCheckoutAttemptsContextProps {
  canVerifyAttempt: () => boolean;
  isAddressElementRequired: boolean;
  isExpressMode: boolean;
  isMemberUsingDefaultPaymentMethod: boolean;
  isOrderRecurring: boolean;
  isPaymentElementReady: boolean;
  isPaymentMethodComplete: boolean;
  /** Whether Stripe's onChange has fired at least once, providing payment method info */
  isPaymentMethodLoaded: boolean;
  /** Whether a payment attempt is currently being processed (submitting, confirming, verifying) */
  isProcessingAttempt: boolean;
  isZeroAmount: boolean;
  memberAddress: MemberAddressState;
  onStageChange: OnStageChange;
  paymentMethodType: string;
  setIsMemberUsingDefaultPaymentMethod: (value: boolean) => void;
  setIsPaymentElementReady: (value: boolean) => void;
  setIsPaymentMethodComplete: (value: boolean) => void;
  setIsPaymentMethodLoaded: (value: boolean) => void;
  setMemberAddress: (address: MemberAddressState) => void;
  setPaymentMethodType: (type: string) => void;
  stripeElementsMode: StripeElementsMode;
  submitAttempt: (data: Record<string, unknown>) => Promise<void>;
  verifyAttempt: (params: VerifyAttemptProps) => Promise<void>;
}

export const PaywallCheckoutAttemptsContext =
  createContext<PaywallCheckoutAttemptsContextValue | null>(null);

PaywallCheckoutAttemptsContext.displayName = "PaywallCheckoutAttemptsContext";

export const usePaywallCheckoutAttemptsContextOrNull = () =>
  useContext(PaywallCheckoutAttemptsContext);

export const usePaywallCheckoutAttemptsContext = () => {
  const value = usePaywallCheckoutAttemptsContextOrNull();
  if (!value) {
    throw new Error(
      "usePaywallCheckoutAttemptsContext must be used within PaywallCheckoutAttemptsProvider",
    );
  }
  return value;
};
